<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Progress Linear Component
      p.text--secondary A horizontal progress bar that displays progress for determinate and indeterminate operations. Built on Vuetify's VProgressLinear component and registered as yz-progress-linear in the YDS component system.

    // Basic Examples
    .component-section.mb-8#basic-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-examples')") Basic Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in basicVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            // Progress Implementation
            yz-progress-linear(v-bind="variation.props")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Determinate Examples
    .component-section.mb-8#determinate-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('determinate-examples')") Determinate Progress
      .variations-grid
        .variation-item.mb-6(v-for="variation in determinateVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            // Progress Implementation
            .mb-2(v-if="variation.showValue")
              span.text-caption Progress: {{ variation.value }}%
            yz-progress-linear(v-bind="variation.props" :value="variation.value")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Color Examples
    .component-section.mb-8#color-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('color-examples')") Color Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in colorVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            // Progress Implementation
            .mb-3(v-for="progress in variation.progresses" :key="progress.label")
              .mb-1
                span.text-caption {{ progress.label }}
              yz-progress-linear(v-bind="progress.props" :value="progress.value")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Height & Rounded Examples
    .component-section.mb-8#height-rounded-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('height-rounded-examples')") Height & Rounded Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in heightVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            // Progress Implementation
            .mb-3(v-for="progress in variation.progresses" :key="progress.label")
              .mb-1
                span.text-caption {{ progress.label }}
              yz-progress-linear(v-bind="progress.props" :value="progress.value")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Buffer Examples
    .component-section.mb-8#buffer-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('buffer-examples')") Buffer Progress
      .variations-grid
        .variation-item.mb-6(v-for="variation in bufferVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            // Progress Implementation
            .mb-1
              span.text-caption Value: {{ variation.value }}% | Buffer: {{ variation.bufferValue }}%
            yz-progress-linear(v-bind="variation.props" :value="variation.value" :buffer-value="variation.bufferValue")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Stream & Query Examples
    .component-section.mb-8#stream-query-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('stream-query-examples')") Stream & Query
      .variations-grid
        .variation-item.mb-6(v-for="variation in streamVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            // Progress Implementation
            yz-progress-linear(v-bind="variation.props")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Reverse & Striped Examples
    .component-section.mb-8#reverse-striped-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('reverse-striped-examples')") Reverse & Striped
      .variations-grid
        .variation-item.mb-6(v-for="variation in reverseVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            // Progress Implementation
            yz-progress-linear(v-bind="variation.props" :value="variation.value")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Interactive Demo
    .component-section.mb-8#interactive-demo
      h3.mb-4.cursor-pointer(@click="navigateToSection('interactive-demo')") Interactive Demo
      .variation-item
        .variation-header.mb-3
          h4 Controllable Progress
          p.text--secondary.caption Use the slider to control the progress value

        .variation-preview.mb-4.pa-4.border.rounded
          .mb-4
            .mb-2
              span.font-weight-medium Interactive Progress
              small.text--secondary.ml-2 Current value: {{ interactiveValue }}%
            yz-progress-linear(color="primary" :value="interactiveValue" height="8")

          .d-flex.align-center.gap-2
            yz-btn(label="Reset" size="sm" outlined @click="interactiveValue = 0")
            yz-btn(label="25%" size="sm" outlined @click="interactiveValue = 25")
            yz-btn(label="50%" size="sm" outlined @click="interactiveValue = 50")
            yz-btn(label="75%" size="sm" outlined @click="interactiveValue = 75")
            yz-btn(label="100%" size="sm" outlined @click="interactiveValue = 100")

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === interactiveCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === interactiveCode ? 'success' : ''" @click="copyCode(interactiveCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ interactiveCode }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API

      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in componentProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      h4.mb-3 Slots
      .slots-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot
                th Description
            tbody
              tr(v-for="slot in componentSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}

      h4.mb-3 Common Use Cases
      .use-cases-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Scenario
                th Recommended Approach
                th Example
            tbody
              tr(v-for="useCase in useCases" :key="useCase.scenario")
                td.font-weight-medium.text-no-wrap {{ useCase.scenario }}
                td.text--secondary {{ useCase.approach }}
                td.single-line {{ useCase.example }}

      h4.mb-3 Implementation Notes
      .notes-section
        v-alert(type="info" outlined)
          ul.mb-0
            li
              strong Indeterminate:
              | Use for unknown duration operations like initial page loads or background processes
            li
              strong Determinate:
              | Use when you know the total work and can calculate percentage (e.g., file uploads, form steps)
            li
              strong Buffer:
              | Shows both current progress and buffered/preloaded amount (useful for media players, downloads)
            li
              strong Color:
              | Supports Vuetify color names (primary, success, error, warning, info) and hex values
            li
              strong Height:
              | Default is 4px. Increase for more prominent displays or to show value text inside
            li
              strong Stream & Query:
              | Use stream for continuous loading (e.g., live data), query for discrete checks (e.g., polling)
            li
              strong Accessibility:
              | Component automatically includes ARIA attributes for screen readers
            li
              strong Registration:
              | This component is Vuetify's VProgressLinear registered as yz-progress-linear in the YDS system
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      interactiveValue: 45
    }
  },
  computed: {
    basicVariations() {
      return [
        {
          name: 'Indeterminate Progress',
          id: 'indeterminate-progress',
          description: 'Loading indicator for operations with unknown duration',
          label: 'Indeterminate (Loading)',
          hint: 'Use for initial loads or background processes',
          props: {
            indeterminate: true,
            color: 'primary'
          },
          code: `yz-progress-linear(indeterminate color="primary")`
        },
        {
          name: 'Default Determinate',
          id: 'default-determinate',
          description: 'Progress bar with a specific value',
          label: 'Determinate Progress',
          hint: '50% complete',
          props: {
            value: 50,
            color: 'primary'
          },
          code: `yz-progress-linear(:value="50" color="primary")`
        }
      ]
    },
    determinateVariations() {
      return [
        {
          name: 'Low Progress',
          id: 'low-progress',
          description: 'Progress bar at 25% completion',
          label: 'Low Progress (25%)',
          hint: 'Early stage of operation',
          props: {
            color: 'primary'
          },
          value: 25,
          showValue: true,
          code: `yz-progress-linear(:value="25" color="primary")`
        },
        {
          name: 'Medium Progress',
          id: 'medium-progress',
          description: 'Progress bar at 50% completion',
          label: 'Medium Progress (50%)',
          hint: 'Halfway through',
          props: {
            color: 'primary'
          },
          value: 50,
          showValue: true,
          code: `yz-progress-linear(:value="50" color="primary")`
        },
        {
          name: 'High Progress',
          id: 'high-progress',
          description: 'Progress bar at 75% completion',
          label: 'High Progress (75%)',
          hint: 'Nearly complete',
          props: {
            color: 'primary'
          },
          value: 75,
          showValue: true,
          code: `yz-progress-linear(:value="75" color="primary")`
        },
        {
          name: 'Complete Progress',
          id: 'complete-progress',
          description: 'Progress bar at 100% completion',
          label: 'Complete (100%)',
          hint: 'Operation finished',
          props: {
            color: 'success'
          },
          value: 100,
          showValue: true,
          code: `yz-progress-linear(:value="100" color="success")`
        }
      ]
    },
    colorVariations() {
      return [
        {
          name: 'Semantic Colors',
          id: 'semantic-colors',
          description: 'Using Vuetify semantic color names',
          label: 'Semantic Color Variants',
          hint: 'primary, success, warning, error, info',
          progresses: [
            { label: 'Primary (60%)', props: { color: 'primary' }, value: 60 },
            { label: 'Success (80%)', props: { color: 'success' }, value: 80 },
            { label: 'Warning (50%)', props: { color: 'warning' }, value: 50 },
            { label: 'Error (30%)', props: { color: 'error' }, value: 30 },
            { label: 'Info (70%)', props: { color: 'info' }, value: 70 }
          ],
          code: `yz-progress-linear(:value="60" color="primary")
yz-progress-linear(:value="80" color="success")
yz-progress-linear(:value="50" color="warning")
yz-progress-linear(:value="30" color="error")
yz-progress-linear(:value="70" color="info")`
        },
        {
          name: 'Custom Background Color',
          id: 'custom-background-color',
          description: 'Using background-color prop for custom track color',
          label: 'Custom Background Color',
          hint: 'background-color prop',
          progresses: [
            { label: 'Light Background', props: { color: 'primary', backgroundColor: 'grey lighten-3' }, value: 60 },
            { label: 'Colored Background', props: { color: 'success', backgroundColor: 'green lighten-4' }, value: 75 }
          ],
          code: `yz-progress-linear(:value="60" color="primary" background-color="grey lighten-3")
yz-progress-linear(:value="75" color="success" background-color="green lighten-4")`
        }
      ]
    },
    heightVariations() {
      return [
        {
          name: 'Height Variants',
          id: 'height-variants',
          description: 'Different heights for various UI needs',
          label: 'Custom Heights',
          hint: 'height prop in pixels',
          progresses: [
            { label: 'Thin (4px - default)', props: { color: 'primary', height: '4' }, value: 65 },
            { label: 'Medium (8px)', props: { color: 'primary', height: '8' }, value: 65 },
            { label: 'Thick (12px)', props: { color: 'primary', height: '12' }, value: 65 },
            { label: 'Extra Thick (20px)', props: { color: 'primary', height: '20' }, value: 65 }
          ],
          code: `yz-progress-linear(:value="65" color="primary" height="4")
yz-progress-linear(:value="65" color="primary" height="8")
yz-progress-linear(:value="65" color="primary" height="12")
yz-progress-linear(:value="65" color="primary" height="20")`
        },
        {
          name: 'Rounded Variants',
          id: 'rounded-variants',
          description: 'Rounded corners for different styles',
          label: 'Rounded Progress Bars',
          hint: 'rounded prop',
          progresses: [
            { label: 'Default (no rounded)', props: { color: 'primary', height: '10' }, value: 70 },
            { label: 'Rounded', props: { color: 'success', height: '10', rounded: true }, value: 70 }
          ],
          code: `yz-progress-linear(:value="70" color="primary" height="10")
yz-progress-linear(:value="70" color="success" height="10" rounded)`
        }
      ]
    },
    bufferVariations() {
      return [
        {
          name: 'Basic Buffer',
          id: 'basic-buffer',
          description: 'Shows both current progress and buffered amount',
          label: 'Buffer Progress',
          hint: 'Useful for media players and downloads',
          props: {
            color: 'primary'
          },
          value: 45,
          bufferValue: 75,
          code: `yz-progress-linear(:value="45" :buffer-value="75" color="primary")`
        },
        {
          name: 'Video Buffering',
          id: 'video-buffering',
          description: 'Example of video playback with buffering',
          label: 'Video Playback Simulation',
          hint: 'Playback at 30%, buffered to 60%',
          props: {
            color: 'red',
            height: '6'
          },
          value: 30,
          bufferValue: 60,
          code: `yz-progress-linear(:value="30" :buffer-value="60" color="red" height="6")`
        }
      ]
    },
    streamVariations() {
      return [
        {
          name: 'Stream Animation',
          id: 'stream-animation',
          description: 'Continuous streaming animation effect',
          label: 'Stream Mode',
          hint: 'For live data streaming',
          props: {
            stream: true,
            color: 'primary'
          },
          code: `yz-progress-linear(stream color="primary")`
        },
        {
          name: 'Query Animation',
          id: 'query-animation',
          description: 'Discrete query animation for polling operations',
          label: 'Query Mode',
          hint: 'For periodic checks',
          props: {
            query: true,
            color: 'info'
          },
          code: `yz-progress-linear(query color="info")`
        }
      ]
    },
    reverseVariations() {
      return [
        {
          name: 'Reverse Direction',
          id: 'reverse-direction',
          description: 'Progress fills from right to left',
          label: 'Reverse Progress',
          hint: 'reverse prop',
          props: {
            reverse: true,
            color: 'primary'
          },
          value: 60,
          code: `yz-progress-linear(:value="60" reverse color="primary")`
        },
        {
          name: 'Striped Pattern',
          id: 'striped-pattern',
          description: 'Progress bar with animated stripes',
          label: 'Striped Progress',
          hint: 'striped prop',
          props: {
            striped: true,
            color: 'success',
            height: '8'
          },
          value: 70,
          code: `yz-progress-linear(:value="70" striped color="success" height="8")`
        }
      ]
    },
    interactiveCode() {
      return `// Template
yz-progress-linear(color="primary" :value="progressValue" height="8")

// Script
data() {
  return {
    progressValue: 45
  }
}`
    },
    componentProps() {
      return [
        {
          name: 'value',
          type: 'Number',
          default: '0',
          description: 'Progress value from 0 to 100. Required for determinate mode.'
        },
        {
          name: 'indeterminate',
          type: 'Boolean',
          default: 'false',
          description: 'Displays continuous animation for operations with unknown duration'
        },
        {
          name: 'color',
          type: 'String',
          default: 'primary',
          description: 'Progress bar color. Supports Vuetify colors (primary, success, error, etc.) and hex values'
        },
        {
          name: 'backgroundColor',
          type: 'String',
          default: 'undefined',
          description: 'Background track color. Supports Vuetify colors and hex values'
        },
        {
          name: 'backgroundOpacity',
          type: 'Number | String',
          default: 'undefined',
          description: 'Opacity of the background track (0 to 1)'
        },
        {
          name: 'height',
          type: 'Number | String',
          default: '4',
          description: 'Height of the progress bar in pixels'
        },
        {
          name: 'bufferValue',
          type: 'Number',
          default: '100',
          description: 'Buffer value from 0 to 100. Shows amount preloaded/buffered'
        },
        {
          name: 'stream',
          type: 'Boolean',
          default: 'false',
          description: 'Displays continuous streaming animation'
        },
        {
          name: 'query',
          type: 'Boolean',
          default: 'false',
          description: 'Displays discrete query animation for polling operations'
        },
        {
          name: 'rounded',
          type: 'Boolean',
          default: 'false',
          description: 'Applies rounded corners to the progress bar'
        },
        {
          name: 'striped',
          type: 'Boolean',
          default: 'false',
          description: 'Applies animated striped pattern to the progress bar'
        },
        {
          name: 'reverse',
          type: 'Boolean',
          default: 'false',
          description: 'Reverses the progress direction (right to left)'
        },
        {
          name: 'absolute',
          type: 'Boolean',
          default: 'false',
          description: 'Positions the progress bar absolutely'
        },
        {
          name: 'active',
          type: 'Boolean',
          default: 'true',
          description: 'Controls visibility of the progress bar'
        },
        {
          name: 'bottom',
          type: 'Boolean',
          default: 'false',
          description: 'Aligns to bottom when using absolute positioning'
        },
        {
          name: 'top',
          type: 'Boolean',
          default: 'false',
          description: 'Aligns to top when using absolute positioning'
        },
        {
          name: 'fixed',
          type: 'Boolean',
          default: 'false',
          description: 'Uses fixed positioning'
        }
      ]
    },
    componentSlots() {
      return [
        {
          name: 'default',
          description: 'Custom content to display inside the progress bar (visible when height is sufficient)'
        }
      ]
    },
    useCases() {
      return [
        {
          scenario: 'Page loading',
          approach: 'Use indeterminate at top of page',
          example: 'yz-progress-linear(indeterminate color="primary" absolute top)'
        },
        {
          scenario: 'File upload',
          approach: 'Use determinate with value bound to upload progress',
          example: 'yz-progress-linear(:value="uploadProgress" color="success")'
        },
        {
          scenario: 'Multi-step form',
          approach: 'Calculate percentage based on completed steps',
          example: 'yz-progress-linear(:value="(currentStep / totalSteps) * 100")'
        },
        {
          scenario: 'Video playback',
          approach: 'Use buffer-value for buffered amount',
          example: 'yz-progress-linear(:value="playback" :buffer-value="buffered")'
        },
        {
          scenario: 'Background process',
          approach: 'Use query or stream animation',
          example: 'yz-progress-linear(query color="info")'
        },
        {
          scenario: 'Download progress',
          approach: 'Show percentage with colored bar',
          example: 'yz-progress-linear(:value="downloadPercent" color="primary" height="8")'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.gap-2 {
  gap: 8px;
}
</style>
